import { differenceInDays } from 'date-fns';
import { safeMoneyFromCents } from '@libs/services/money';
import { Order, Cart } from 'src/trpc';

// Extract Quote type from Order's quotedPricingDetails
type QuotedPricingDetails = NonNullable<Order['quotedPricingDetails']>;
type Quote = NonNullable<QuotedPricingDetails['quote']>;

// Extract LineItems type from Cart
type CartLineItems = NonNullable<Cart['lineItems']>;

interface PayAsYouGoPricingCardProps {
  quote?: Quote;
  lineItems: CartLineItems;
  dropOffDate?: string;
  pickupDate?: string;
}

export const PayAsYouGoPricingCard = ({
  quote,
  lineItems,
  dropOffDate,
  pickupDate,
}: PayAsYouGoPricingCardProps) => {
  // Determine if we should use quote data or fallback to lineItems
  const hasQuote = !!quote;

  // Extract data from quote OR fallback to lineItems
  const isMinimumApplied = hasQuote
    ? quote.weightData.isMinimumWeightApplied
    : lineItems.isMinimumWeightApplied;

  const minimumWeight = hasQuote
    ? quote.weightData.minimumWeightInTons
    : lineItems.minimumWeightInTons || 0;

  // IMPORTANT: effectiveWeight must use minimumWeight when minimum is applied
  const effectiveWeight = hasQuote
    ? quote.pricing.materials.effectiveWeightInTons
    : isMinimumApplied && minimumWeight
      ? minimumWeight
      : lineItems.actualWeightInTons || 0;

  const perTonRate = hasQuote
    ? quote.pricing.materials.perTonRateInCents
    : lineItems.pricePerTonInCents || 0;

  const containers = hasQuote ? quote.pricing.containers : lineItems.containers;

  // Calculate service period in days
  const calculateServiceDays = () => {
    if (!pickupDate || !dropOffDate) return null;
    const days = Math.max(0, differenceInDays(new Date(pickupDate), new Date(dropOffDate)));
    return days;
  };

  const serviceDays = calculateServiceDays();

  // Calculate daily rate
  const getDailyRate = () => {
    if (!containers || !containers[0]) return 0;
    if (hasQuote) {
      const quoteContainers = quote!.pricing.containers;
      return Math.round(
        (quoteContainers[0].adjustedPriceInCents || 0) / (quoteContainers[0].totalDays || 1),
      );
    } else {
      const lineItemsContainers = lineItems.containers;
      return Math.round(
        (lineItemsContainers?.[0].totalInCents || 0) / (lineItemsContainers?.[0].totalDays || 1),
      );
    }
  };

  const dailyRate = getDailyRate();

  // Calculate total container cost
  const getTotalContainerCost = () => {
    if (!containers) return 0;
    if (hasQuote) {
      const quoteContainers = quote!.pricing.containers;
      return quoteContainers.reduce((sum, c) => sum + (c.adjustedPriceInCents || 0), 0);
    } else {
      const lineItemsContainers = lineItems.containers;
      return lineItemsContainers?.reduce((sum, c) => sum + (c.totalInCents || 0), 0) || 0;
    }
  };

  const totalContainerCost = getTotalContainerCost();

  // Get total days across all containers
  const getTotalDays = () => {
    if (!containers) return 0;
    return containers.reduce((sum, c) => sum + c.totalDays, 0);
  };

  const totalDays = getTotalDays();

  // Get material weight fee
  const getMaterialWeightFee = () => {
    return hasQuote ? quote.pricing.materials.totalPriceInCents : lineItems.materials || 0;
  };

  const materialWeightFee = getMaterialWeightFee();

  return (
    <div className="card flex flex-col gap-4 justify-start w-full p-4 mt-2">
      <div className="flex flex-col gap-2 w-full self-start">
        <h3 className="font-semibold text-lg text-primary-900">Pay-As-You-Go Pricing</h3>

        {/* Service Details Grid */}
        <div className="grid grid-cols-2 gap-4 w-full">
          <div className="flex flex-col w-full">
            <p className="label text-sm">Service Period</p>
            {serviceDays !== null && (
              <p className="text-text-primary text-sm font-medium">
                {serviceDays} {serviceDays === 1 ? 'day' : 'days'}
              </p>
            )}
            <p className="text-text-primary text-sm font-medium mt-1">
              Estimated: {effectiveWeight.toFixed(2)} tons
            </p>
          </div>

          <div className="flex flex-col w-full">
            <p className="label text-sm">Rates</p>
            {dailyRate > 0 && (
              <p className="text-text-primary text-sm">
                Daily: {safeMoneyFromCents(dailyRate).toFormat()}/day
              </p>
            )}
            <p className="text-text-primary text-sm mt-1">
              Weight: {safeMoneyFromCents(perTonRate).toFormat()}/ton
            </p>
          </div>
        </div>

        {/* Dumpster info */}
        <div className="mt-2">
          {containers?.map((container) => (
            <div key={container._id}>
              <p className="text-text-primary text-sm">{container.name} Dumpster</p>
              <p className="text-text-light text-xs">
                {container.quantity} dumpster{container.quantity > 1 ? 's' : ''}
              </p>
            </div>
          ))}
        </div>

        {/* Minimum Weight Warning */}
        {isMinimumApplied && (
          <div className="mt-3">
            <div className="flex flex-col gap-2">
              <p className="text-sm text-amber-900 bg-amber-50 border-l-4 border-amber-400 rounded p-2">
                Your order is below our minimum weight requirement of {minimumWeight.toFixed(2)}{' '}
                tons. You will be charged for {minimumWeight.toFixed(2)} tons of material.
              </p>
            </div>
          </div>
        )}

        {/* Pricing Breakdown */}
        <div className="mt-3 pt-3 border-t border-primary-100">
          <div className="flex flex-col text-sm mb-2">
            <div className="flex flex-row justify-between">
              <p>Dumpster Cost</p>
              <p>{safeMoneyFromCents(totalContainerCost).toFormat()}</p>
            </div>
            <p className="text-xs text-text-light">
              {totalDays} day{totalDays !== 1 ? 's' : ''} ×{' '}
              {safeMoneyFromCents(dailyRate).toFormat()}/day
            </p>
          </div>

          <div className="flex flex-col text-sm">
            <div className="flex flex-row justify-between">
              <p>Material Weight Fees</p>
              <p>{safeMoneyFromCents(materialWeightFee).toFormat()}</p>
            </div>
            <p className="text-xs text-text-light">
              {effectiveWeight.toFixed(2)} ton{effectiveWeight !== 1 ? 's' : ''} ×{' '}
              {safeMoneyFromCents(perTonRate).toFormat()}/ton
            </p>
          </div>

          <p className="text-xs text-text-light italic mt-2">
            Final charges based on actual usage.{' '}
            {isMinimumApplied && 'Minimum weight charge applies.'}
          </p>
        </div>
      </div>
    </div>
  );
};
